/**
 * `<caos-icon>` — one glyph from the platform's vocabulary, placed on a page at a step of the size scale.
 * (Catalog: Data display › Icon — one glyph from the platform's single vocabulary, placed on a page at a step of the size scale.)
 *
 * CAOS-594. The vocabulary already existed and every component draws from it (`icons.ts`), but it was
 * a shared internal, so a page author could not put a glyph on a page. This element does that and
 * nothing else. It draws from the SAME list: there is no second set, and a name the vocabulary does
 * not have draws nothing rather than an empty box.
 *
 * SIZE comes from the platform scale (`src/sizes.ts`, `--caos-size-<step>-height`), and the glyph
 * fills a square box of that length. When `size` is absent or names no step, it is `sm`.
 *
 * COLOUR is the colour of wherever the icon is placed. Every glyph strokes `currentColor`, so the icon
 * adds no colour of its own and reads no colour token.
 *
 * NAME for a screen reader: with a `label` it is an image with that name. Without one it is decorative
 * and hidden from assistive technology, which is right for a glyph beside words that already say it.
 * A name the vocabulary does not have draws nothing, so the icon is hidden then even with a label.
 *
 * Styling seam: `::part(icon)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { sizeRules, type SizeStep } from '../sizes.js';
import { isIconName, makeIcon } from './icons.js';

export const ICON_DEFAULT_SIZE: SizeStep = 'sm';

/** The icon's own styles, as written. */
export const ICON_STYLES = `
  :host { display: inline-flex; vertical-align: middle; flex: none; }
  .icon { display: inline-flex; width: 100%; height: 100%; }
  .icon svg { display: block; width: 100%; height: 100%; }
  ${sizeRules(ICON_DEFAULT_SIZE, (host, length) => `${host} { width: ${length}; height: ${length}; }`)}
`;

const sheet = makeSheet(ICON_STYLES);

export class CaosIcon extends CaosElement {
  static get observedAttributes(): string[] {
    return ['name', 'label'];
  }

  private readonly box: HTMLSpanElement;

  constructor() {
    super(sheet);
    this.box = document.createElement('span');
    this.box.className = 'icon';
    this.box.setAttribute('part', 'icon');
    this.root.append(this.box);
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  private render(): void {
    const name = (this.getAttribute('name') ?? '').trim();
    const drawn = isIconName(name);
    this.box.replaceChildren(...(drawn ? [makeIcon(name)] : []));

    // A label names the glyph, so with no glyph drawn there is no image to announce.
    const label = drawn ? (this.getAttribute('label') ?? '').trim() : '';
    if (label) {
      this.box.setAttribute('role', 'img');
      this.box.setAttribute('aria-label', label);
      this.box.removeAttribute('aria-hidden');
    } else {
      this.box.removeAttribute('role');
      this.box.removeAttribute('aria-label');
      this.box.setAttribute('aria-hidden', 'true');
    }
  }
}

define('caos-icon', CaosIcon);

declare global {
  interface HTMLElementTagNameMap {
    'caos-icon': CaosIcon;
  }
}
